import React, { useState, useEffect, useMemo } from 'react';
import { Box, Container, useTheme, Paper } from '@mui/material';
import { PageHeader } from '../common/PageHeader';
import AdminNavbar from './AdminNavbar';
import HelpOverlay from '../common/HelpOverlay';

/**
 * Standard wrapper for admin and status pages that includes theme,
 * headers, and standardized help functionality
 */
const AdminPageWrapper = ({ 
  children, 
  helpItems = [], 
  title = "",
  showNavbar = true,
  maxWidth = '1600px',
  // Theme props
  darkMode: propsDarkMode,
  toggleDarkMode: propsToggleDarkMode,
  themeVariant: propsThemeVariant
}) => {
  // Dark mode state - use props if provided, otherwise manage locally
  const [darkMode, setDarkMode] = useState(() => {
    // If provided via props, use that
    if (propsDarkMode !== undefined) return propsDarkMode;
    
    // Otherwise check localStorage
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode !== null) {
      return savedMode === 'true';
    }
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  
  // Theme variant state
  const [themeVariant, setThemeVariant] = useState(() => {
    // If provided via props, use that
    if (propsThemeVariant !== undefined) return propsThemeVariant;
    
    // Otherwise check localStorage
    const savedTheme = localStorage.getItem('themeVariant');
    return savedTheme || 'minimalist'; // Default to minimalist theme
  });
  
  // Update local state when props change
  useEffect(() => {
    if (propsDarkMode !== undefined) {
      setDarkMode(propsDarkMode);
    }
    if (propsThemeVariant !== undefined) {
      setThemeVariant(propsThemeVariant);
    }
  }, [propsDarkMode, propsThemeVariant]);
  
  // Toggle dark mode function - use props function if provided, otherwise manage locally
  const toggleDarkMode = () => {
    if (propsToggleDarkMode) {
      propsToggleDarkMode();
    } else {
      setDarkMode(prevMode => {
        const newMode = !prevMode;
        localStorage.setItem('darkMode', newMode.toString());
        return newMode;
      });
    }
  };
  
  // Access the current theme (provided by AppThemeProvider)
  const theme = useTheme();
  
  // Get background color based on theme variant
  const getBackgroundColor = useMemo(() => {
    if (darkMode) {
      if (themeVariant === 'vibrant') {
        return theme.palette.admin?.background || theme.palette.background.default;
      } else {
        switch (themeVariant) {
          case 'minimalist':
            return '#1a1c2a';
          case 'corporate':
            return '#171c21';
          case 'materialYou':
            return '#191c1c';
          default:
            return theme.palette.background.default;
        }
      }
    } else {
      if (themeVariant === 'vibrant') {
        return theme.palette.admin?.background || theme.palette.background.default;
      } else {
        switch (themeVariant) {
          case 'minimalist':
            return '#f7f9fc';
          case 'corporate':
            return '#f5f7fa';
          case 'materialYou':
            return '#f5fafa';
          default:
            return '#f5f5f5';
        }
      }
    }
  }, [darkMode, themeVariant, theme]);
  
  return (
    <Box sx={{
                width: '100%',
                backgroundColor: darkMode ? 'background.default' : '#f5f5f5',
                minHeight: '100vh',
            }}>
      <Paper sx={{
        borderRadius: 0,
        padding: 0
      }}>
      <PageHeader 
        darkMode={darkMode} 
        toggleDarkMode={toggleDarkMode}
        title={title}
        themeVariant={themeVariant} 
      />
      
      {showNavbar && <AdminNavbar />}
      
      <Container sx={{ 
        mt: 4, 
        maxWidth: '100% !important', 
        px: { xs: 1, sm: 2, md: 3 },
        // Add subtle shadow to container based on theme
        ...(themeVariant === 'vibrant' && {
          backgroundColor: theme.palette.admin?.cardBg || (darkMode ? 'rgba(30,30,45,0.7)' : 'rgba(255,255,255,0.8)'),
          borderRadius: '12px',
          boxShadow: darkMode 
            ? '0 4px 20px rgba(0,0,0,0.2)' 
            : '0 4px 20px rgba(0,0,0,0.05)',
          p: 3
        })
      }} className="admin-page-container">
        {/* Add help overlay if help items are provided */}
        {helpItems.length > 0 && (
          <HelpOverlay
            title={`${title || "Page"} Help Guide`}
            buttonId={`${title.toLowerCase().replace(/\s/g, '-')}-help-button`}
            helpItems={helpItems}
          />
        )}
        
        {/* Render children */}
        {children}
      </Container>
      </Paper>
    </Box>
  );
};

export default AdminPageWrapper;